import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/np/Seo";
import SectionHeading from "@/components/np/SectionHeading";
import BookButton from "@/components/np/BookButton";
import Reveal from "@/components/np/Reveal";
import FinalCta from "@/components/home/FinalCta";
import BodyAreaExplorer from "@/components/body/BodyAreaExplorer";
import { bodyAreas, conditionThemes, getTreatment, meta } from "@/content";

const AreaSection = ({ a }) => (
  <Reveal as="article" id={a.slug} aria-labelledby={`area-${a.slug}`} className="scroll-mt-40 rounded-[28px] bg-white p-6 ring-1 ring-line sm:p-8" data-testid={`area-section-${a.slug}`}>
    <h3 id={`area-${a.slug}`} className="font-serif text-3xl text-ink">{a.label}</h3>
    <p className="mt-2 text-muted">{a.lead}</p>
    <ul className="mt-5 grid gap-2 sm:grid-cols-2">
      {a.conditions.map((c) => (
        <li key={c} className="flex gap-2.5 text-[15px] text-ink"><Check className="mt-1 h-4 w-4 shrink-0 text-pine" aria-hidden="true" />{c}</li>
      ))}
    </ul>
    <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-line pt-5">
      <span className="mr-1 text-xs font-semibold uppercase tracking-[0.14em] text-muted">Behandlingar som kan vara aktuella:</span>
      {a.treatments.map((id) => (
        <Link key={id} to={`/behandlingar#${id}`} aria-label={getTreatment(id).name} data-testid={`area-${a.slug}-treatment-${id}`} className="max-w-full rounded-full bg-sage/45 px-3 py-1 text-sm font-medium text-forest [overflow-wrap:anywhere] hover:bg-sage">
          {id === "neuropatiutredning" ? (
            <>
              <span className="sm:hidden">Neuropatiutredning</span>
              <span className="hidden sm:inline">{getTreatment(id).name}</span>
            </>
          ) : getTreatment(id).name}
        </Link>
      ))}
    </div>
  </Reveal>
);

export default function Besvar() {
  return (
    <>
      <Seo {...meta.besvar} />
      <section className="page-hero" aria-labelledby="besvar-title">
        <div className="container-np py-14 sm:py-16">
          <p className="eyebrow">Besvär</p>
          <h1 id="besvar-title" className="mt-4 max-w-3xl font-serif text-5xl leading-[1.02] text-ink sm:text-6xl lg:text-7xl">Var har du ont?</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted">Du behöver inte veta vad besvären heter. Utgå från var det gör ont – så ser du vilka besvär vi arbetar med i området. Den slutliga bedömningen gör läkaren vid besöket.</p>
          <div className="mt-10 max-w-4xl rounded-[32px] bg-white p-6 ring-1 ring-line sm:p-8">
            <BodyAreaExplorer showFigure={false} chipPrefix="besvar-chip" />
          </div>
        </div>
      </section>
      <section className="section pt-4" aria-labelledby="areas-title">
        <div className="container-np">
          <SectionHeading id="areas-title" eyebrow="Kroppsområden" title="Besvär per område" />
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {bodyAreas.map((a) => <AreaSection key={a.slug} a={a} />)}
          </div>
        </div>
      </section>
      <section className="bg-sand/60 py-16 sm:py-20 lg:py-24" aria-labelledby="themes-title">
        <div className="container-np grid gap-10 lg:grid-cols-12">
          <SectionHeading id="themes-title" className="lg:col-span-5" eyebrow="Tillstånd" title="Besvär vi ofta möter" intro="Känner du inte igen dig? Boka en bedömning så går läkaren igenom dina besvär." />
          <div className="lg:col-span-7">
            <ul className="flex flex-wrap gap-2.5">
              {conditionThemes.map((c) => <li key={c} className="rounded-full bg-white px-4 py-2 text-[15px] ring-1 ring-line">{c}</li>)}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <BookButton testId="besvar-book-button" />
              <Link to="/behandlingar" className="link-arrow sm:ml-4" data-testid="besvar-treatments-link">Om behandlingarna <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
      <div className="pt-16 sm:pt-20"><FinalCta /></div>
    </>
  );
}
